import { Link } from "react-router-dom";
import { Wrench, MessageSquare, Calendar, Receipt, User } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import DocsCategoryLayout from "../components/docs/DocsCategoryLayout";
import LuxAccordion from "../components/landing/LuxAccordion";

/**
 * /docs/troubleshooting — issues grouped by domain. Each group is a small
 * subhead with a stack of LuxAccordion items so users can scan headlines
 * fast and only expand the symptom that matches.
 */

const ISSUES = {
  sms: [
    {
      q: "My technician isn't receiving job notifications",
      a: (
        <>
          <p>Three things to check, in order:</p>
          <ul>
            <li><strong>Phone number format.</strong> Open the tech&apos;s profile in <strong>Settings &gt; Team</strong>. Their number must be E.164 (leading <code>+</code> and country code, e.g. <code>+14155551234</code>). Reformat if it&apos;s missing the <code>+</code>.</li>
            <li><strong>SMS template is on.</strong> Go to <strong>Settings &gt; SMS Templates</strong> and confirm the <code>job_scheduled</code> template is toggled <em>Active</em>. Disabled templates don&apos;t fire.</li>
            <li><strong>Carrier filtering.</strong> Some US carriers aggressively filter messages with shortened links. If only some techs aren&apos;t getting messages, ask them to text START to your business number — that whitelists you on their device.</li>
          </ul>
        </>
      ),
    },
    {
      q: "SMS automations aren't firing",
      a: (
        <>
          <ul>
            <li><strong>Verify the trigger event.</strong> Each template binds to one event (<code>quote_sent</code>, <code>invoice_sent</code>, etc.). Make sure the event you expect is the one selected on the template.</li>
            <li><strong>Check it&apos;s saved AND active.</strong> A template can be saved but inactive — both are required for it to fire.</li>
            <li><strong>Check your SMS balance.</strong> If your monthly SMS allotment is exhausted, sends fail silently. <strong>Settings &gt; Phones</strong> shows current usage at the top.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Inbound calls aren't routing correctly",
      a: (
        <>
          <ul>
            <li>Open <strong>Settings &gt; Phones</strong> and check the <strong>Routing Mode</strong> on the affected number.</li>
            <li>For <em>Forward</em> mode, confirm the destination number is in E.164 format (with leading <code>+</code> and country code).</li>
            <li>For <em>IVR</em> mode, click <strong>Edit IVR</strong> and verify each option has a destination assigned. Empty branches fall through to your voicemail.</li>
            <li>For <em>Voice Client</em> mode, the recipient&apos;s browser must be open and signed in to receive the call. If no one&apos;s online, calls bounce to voicemail.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Call masking isn't working for a tech",
      a: (
        <>
          <ul>
            <li><strong>Verify the job is assigned</strong>, not just created. PINs are generated only when a tech is explicitly assigned to a job.</li>
            <li><strong>Check the tech&apos;s phone number</strong> is saved in <strong>Settings &gt; Team</strong>. The masking system needs it to validate the inbound call to the masked number.</li>
            <li><strong>Make sure the job isn&apos;t Completed or Cancelled.</strong> PINs expire automatically when the job closes — that&apos;s by design, so old PINs can&apos;t leak.</li>
          </ul>
        </>
      ),
    },
  ],
  jobs: [
    {
      q: "A job disappeared from the calendar",
      a: (
        <>
          <ul>
            <li>Check the <strong>status filter</strong> at the top of the page. Cancelled jobs are hidden by default.</li>
            <li>Check the <strong>date range</strong>. The default is the current week — switch to <em>Month</em> view if the job is further out.</li>
            <li>Search by customer name on the Jobs list view. If it shows up there but not on the calendar, it&apos;s a filter issue, not a missing job.</li>
            <li>Last resort: check <strong>Settings &gt; Account &gt; Deleted Items</strong>. Deleted jobs are restorable for 30 days.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Tech says they didn't get the job notification",
      a: (
        <>
          <ul>
            <li><strong>Push notification permission.</strong> Ask them to open the mobile app, go to <strong>Settings &gt; Notifications</strong>, and confirm push is enabled. iOS/Android may have prompted them to deny on first install.</li>
            <li><strong>Active account.</strong> Verify the tech is <em>Active</em> in your <strong>Settings &gt; Team</strong> list. Deactivated members don&apos;t receive any notifications.</li>
            <li><strong>Reinstall trick.</strong> If push is enabled but messages still aren&apos;t arriving, have them sign out and back in — this re-registers their push token with our server.</li>
          </ul>
        </>
      ),
    },
    {
      q: "I can't reschedule a job",
      a: (
        <>
          <p>Only jobs in <em>Scheduled</em> status can be drag-and-dropped on the calendar. Jobs that are <em>In Progress</em> or <em>Completed</em> are locked.</p>
          <ul>
            <li>If you need to reschedule an In Progress job, ask the tech to tap <strong>Pause</strong> in the mobile app first — that flips it back to Scheduled.</li>
            <li>If you need to reschedule a Completed job, the cleanest path is to mark it Cancelled and create a new job with the same customer and service.</li>
          </ul>
        </>
      ),
    },
  ],
  invoices: [
    {
      q: "Customer says the payment link doesn't work",
      a: (
        <>
          <ul>
            <li><strong>Stripe Connect must be active.</strong> Go to <strong>Settings &gt; Payments</strong> and confirm the Stripe connection is in the green <em>Connected</em> state. If it&apos;s in <em>Setup Incomplete</em>, finish the Stripe onboarding flow — payment links don&apos;t work until you do.</li>
            <li><strong>Invoice must be Sent, not Draft.</strong> Draft invoices don&apos;t generate active pay links. From the invoice page, click <strong>Send</strong> (or <strong>Mark as Sent</strong> if you sent it through another channel).</li>
            <li><strong>Test the link yourself.</strong> Open the invoice, click <strong>Copy Pay Link</strong>, paste into a private browser window. If the link page loads but card entry fails, that&apos;s on Stripe&apos;s side — usually a card issue, not a Fieldz issue.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Invoice shows as Paid but I didn't receive the money",
      a: (
        <>
          <p>Stripe payouts run on a schedule — typically 2 business days from the charge for established US accounts, longer if your account is new or your country has a different default.</p>
          <ul>
            <li>Open your <a href="https://dashboard.stripe.com/payouts" target="_blank" rel="noopener noreferrer">Stripe dashboard</a> and check the Payouts page. The next scheduled payout is shown there with the expected arrival date.</li>
            <li>If a charge succeeded but the payout is delayed beyond 5 business days, contact Stripe support — Fieldz doesn&apos;t hold funds, they go straight to your Stripe account.</li>
          </ul>
        </>
      ),
    },
    {
      q: "I sent an estimate but the customer never got it",
      a: (
        <>
          <ul>
            <li><strong>Check the customer&apos;s email</strong> in their profile. A typo here means the email bounces silently. Look for the <em>Email bounced</em> badge on the customer row.</li>
            <li><strong>Check the SMS automation.</strong> If <code>quote_sent</code> is enabled, customers also get a follow-up SMS with the link. Confirm the customer&apos;s phone number is correct and the template is Active.</li>
            <li><strong>Resend.</strong> From the estimate, click <strong>Resend</strong>. The same email + SMS bundle goes out again.</li>
          </ul>
        </>
      ),
    },
  ],
  account: [
    {
      q: "A team member can't log in",
      a: (
        <>
          <ul>
            <li>Open <strong>Settings &gt; Team</strong>. Members in <em>Pending</em> status haven&apos;t accepted the invite yet — click <strong>Resend Invite</strong> to send a fresh setup link.</li>
            <li>If they&apos;re <em>Active</em> but still can&apos;t sign in, have them use <Link to="/forgot-password">/forgot-password</Link> to reset. Most issues at this stage are forgotten passwords.</li>
            <li>If reset emails aren&apos;t arriving, check the email is correct in their profile, then check spam. Reset links are valid 1 hour.</li>
          </ul>
        </>
      ),
    },
    {
      q: "I accidentally deleted a job",
      a: (
        <>
          <p>Deleted jobs can be restored within <strong>30 days</strong>.</p>
          <ul>
            <li>Go to <strong>Settings &gt; Account &gt; Deleted Items</strong>.</li>
            <li>Find the job in the list. Filter by date if there are many.</li>
            <li>Click <strong>Restore</strong>. The job comes back exactly as it was, with the same status, assignments, and history.</li>
          </ul>
          <p>After 30 days deleted items are permanently purged and can&apos;t be recovered.</p>
        </>
      ),
    },
    {
      q: "My plan didn't upgrade after payment",
      a: (
        <>
          <ul>
            <li><strong>Hard refresh.</strong> The most common cause is a stale page. <kbd>Cmd/Ctrl + Shift + R</kbd> to force-reload.</li>
            <li>Open <strong>Settings &gt; My Plan</strong>. The current plan should match what you just paid for. If it does, the upgrade went through and the issue was just caching.</li>
            <li>If it still shows the old plan after refresh, the Stripe webhook may have failed. Wait 2 minutes (Stripe retries automatically), then refresh again.</li>
            <li>Still wrong after that? <Link to="/contact">Contact support</Link> with your account email and the Stripe charge ID — we&apos;ll fix it manually.</li>
          </ul>
        </>
      ),
    },
  ],
};

const Group = ({ icon: Icon, title, items, idPrefix }) => (
  <div className="mt-2">
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: "rgba(201, 168, 76, 0.10)", border: "1px solid rgba(201, 168, 76, 0.20)" }}
      >
        <Icon className="w-5 h-5" style={{ color: "var(--lux-gold)" }} />
      </div>
      <h3 style={{ marginTop: 0, marginBottom: 0 }}>{title}</h3>
    </div>
    <div className="space-y-3">
      {items.map((item, i) => (
        <LuxAccordion
          key={i}
          question={item.q}
          testIdPrefix={`troubleshoot-${idPrefix}`}
          testIdSuffix={String(i)}
        >
          {item.a}
        </LuxAccordion>
      ))}
    </div>
  </div>
);

const ARTICLES = [
  {
    id: "sms-and-calls",
    title: "SMS & Calls",
    body: <Group icon={MessageSquare} title="" items={ISSUES.sms} idPrefix="sms" />,
  },
  {
    id: "jobs-and-scheduling",
    title: "Jobs & Scheduling",
    body: <Group icon={Calendar} title="" items={ISSUES.jobs} idPrefix="jobs" />,
  },
  {
    id: "invoices-and-payments",
    title: "Invoices & Payments",
    body: <Group icon={Receipt} title="" items={ISSUES.invoices} idPrefix="invoice" />,
  },
  {
    id: "account-and-access",
    title: "Account & Access",
    body: <Group icon={User} title="" items={ISSUES.account} idPrefix="account" />,
  },
];

const DocsTroubleshootingPage = () => (
  <DocsCategoryLayout
    category={{
      id: "troubleshooting",
      name: "Troubleshooting",
      icon: Wrench,
      intro: "Quick fixes for the most common issues. Browse by domain, expand the symptom that matches yours, and follow the checklist.",
    }}
    articles={ARTICLES}
  />
);

export default DocsTroubleshootingPage;
